import {Text} from '@dropbox/dig-components/typography';
import tabStyles from 'components/DSYS/GroupTabs.module.css';
import {forwardRef, useEffect, useImperativeHandle, useRef} from 'react';

import styles from './Apps.module.css';
import {useWheelAxisLock} from './useWheelAxisLock';

/**
 * Jump links for each shelf, in one row that scrolls sideways rather than collapsing into a
 * menu. Keeps the active tab in view as the page's scroll position moves it.
 */
export const AppOrgTabs = forwardRef<
  HTMLDivElement,
  {
    activeId: string | null;
    onSelect: (id: string) => void;
    /** Disabled tabs stay in place (keeping the row stable) but can't be chosen. */
    tabs: {disabled?: boolean; id: string; label: string}[];
  }
>(({activeId, onSelect, tabs}, ref) => {
  const railRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  useImperativeHandle(ref, () => railRef.current as HTMLDivElement);
  useWheelAxisLock(railRef);

  useEffect(() => {
    const rail = railRef.current;
    const tab = activeId ? tabRefs.current[activeId] : null;
    if (!rail || !tab) return;
    const railRect = rail.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();
    const padding = parseFloat(getComputedStyle(rail).paddingLeft) || 0;
    if (tabRect.left < railRect.left + padding) {
      rail.scrollLeft -= railRect.left + padding - tabRect.left;
    } else if (tabRect.right > railRect.right - padding) {
      rail.scrollLeft += tabRect.right - (railRect.right - padding);
    }
  }, [activeId]);

  return (
    <div ref={railRef} className={styles.appOrgTabs}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          ref={(element) => {
            tabRefs.current[tab.id] = element;
          }}
          type="button"
          className={[
            tabStyles.groupTab,
            activeId === tab.id ? tabStyles.groupTabActive : '',
            tab.disabled ? tabStyles.groupTabDisabled : '',
          ]
            .filter(Boolean)
            .join(' ')}
          aria-current={activeId === tab.id ? 'location' : undefined}
          disabled={tab.disabled}
          onClick={() => onSelect(tab.id)}
        >
          <Text className={tabStyles.groupTabLabel}>{tab.label}</Text>
        </button>
      ))}
    </div>
  );
});

AppOrgTabs.displayName = 'AppOrgTabs';
